import { Injectable, inject } from '@angular/core';

import { I18nService } from '@gruposaltaedu/liv-i18n/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  finalize,
  map,
  of,
  shareReplay,
  tap
} from 'rxjs';
import { AcademicYearModel } from 'src/app/core/models/academic-year.model';
import { ApiLiv } from 'src/app/core/services/api/api-liv.service';
import { AcademicYearsQuery } from 'src/app/core/services/api/requests/graphql/academic-year.gql';

@Injectable({ providedIn: 'root' })
export class AcademicYearsStateService {
  private readonly apiLiv = inject(ApiLiv);
  private readonly i18n = inject(I18nService);

  private readonly academicYearsSubject = new BehaviorSubject<
    AcademicYearModel[]
  >([]);
  readonly academicYears$: Observable<AcademicYearModel[]> =
    this.academicYearsSubject.asObservable();

  private readonly selectedAcademicYearSubject =
    new BehaviorSubject<AcademicYearModel>(null);
  readonly selectedAcademicYear$: Observable<AcademicYearModel> =
    this.selectedAcademicYearSubject.asObservable();

  private readonly activeAcademicYearSubject =
    new BehaviorSubject<AcademicYearModel>(null);
  readonly activeAcademicYear$: Observable<AcademicYearModel> =
    this.activeAcademicYearSubject.asObservable();

  private readonly isLoadingSubject = new BehaviorSubject(false);
  readonly isLoading$ = this.isLoadingSubject.asObservable();

  private academicYearsRequest$: Observable<AcademicYearModel[]> | null = null;

  get exists$(): Observable<boolean> {
    return this.academicYears$.pipe(map((data) => data.length > 0));
  }

  get academicYearsSnapshot(): AcademicYearModel[] {
    return this.academicYearsSubject.getValue();
  }

  get selectedAcademicYearSnapshot(): AcademicYearModel {
    return this.selectedAcademicYearSubject.getValue();
  }

  get activeAcademicYearSnapshot(): AcademicYearModel {
    return this.activeAcademicYearSubject.getValue();
  }

  /** Fetches academic years once and caches them; a no-op if already loaded or in flight. */
  loadAcademicYears(): Observable<AcademicYearModel[]> {
    if (this.academicYearsSnapshot.length > 0) {
      return of(this.academicYearsSnapshot);
    }

    if (!this.academicYearsRequest$) {
      this.setLoading(true);
      const gql = AcademicYearsQuery.get(this.i18n.currentLanguage());

      this.academicYearsRequest$ = this.apiLiv
        .graphql<{ anoLetivos: AcademicYearModel[] }>(gql)
        .pipe(
          map((data) =>
            (data?.anoLetivos ?? []).toSorted(
              (a, b) => Number(a.ano) - Number(b.ano)
            )
          ),
          catchError(() => {
            this.academicYearsRequest$ = null;
            return of<AcademicYearModel[]>([]);
          }),
          tap((academicYears) => this.persist(academicYears)),
          finalize(() => this.setLoading(false)),
          shareReplay(1)
        );
    }

    return this.academicYearsRequest$;
  }

  persist(academicYears: AcademicYearModel[]): void {
    this.academicYearsSubject.next(academicYears);

    if (academicYears.length > 0) {
      // Falls back to the most recent year when none is flagged ativo
      const activeAcademicYear =
        academicYears.find((year) => year.ativo) ?? academicYears.at(-1);
      this.activeAcademicYearSubject.next(activeAcademicYear);
      this.selectedAcademicYearSubject.next(activeAcademicYear);
    }
  }

  persistSelected(academicYear: AcademicYearModel): void {
    this.selectedAcademicYearSubject.next(academicYear);
  }

  setLoading(loading: boolean): void {
    this.isLoadingSubject.next(loading);
  }

  reset(): void {
    this.academicYearsSubject.next([]);
    this.selectedAcademicYearSubject.next(null);
    this.activeAcademicYearSubject.next(null);
    this.isLoadingSubject.next(false);
    this.academicYearsRequest$ = null;
  }
}
